import { generateCsv } from "@msuite/utils/csv";
import { getLocalStorageKeysByPrefix } from "@msuite/utils/local-storage";
import { guardString } from "@msuite/utils/strings";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Button } from "../../button";
import { Icon, icon } from "../../icons";
import { useTranslation } from "../../localization";
import { Menu, type MenuItemType } from "../../menu";
import type { MenuProps } from "../../menu/menu";
import { PAGINATION_SIZES } from "./constants";
import { useDataTableContext } from "./context";

/** Props Interface */
interface DataTableMenuProps {
	menuItems?: ReturnType<MenuProps["items"]>;
}

export function DataTableMenu({ menuItems = [] }: DataTableMenuProps) {
	/** Context */
	const {
		table,
		persistenceKey,
		refetch,
		queryKey,
		handleOnToggleMonospaced,
		isMonospaced,
		showGroupingHeader,
		setShowGroupingHeader,
	} = useDataTableContext();

	/** Hooks */
	const qc = useQueryClient();
	const { t } = useTranslation();

	/** Functions */
	function handleOnReset(persistenceKey: string) {
		const keys = getLocalStorageKeysByPrefix(persistenceKey);
		for (const key of keys) {
			localStorage.removeItem(key);
		}
		window.location.reload();
	}

	function handleOnGenerateCsv() {
		const columns = table.getAllColumns();
		generateCsv({
			items: table.getPrePaginatedRowModel().rows.map((row) => {
				const item: Record<string, unknown> = {};
				for (const column of columns) {
					if (column.getIsVisible()) {
						if (!column.columnDef.id) continue;
						if (column.columnDef.id === "actions") continue;
						const header = column.columnDef.id as string;
						item[header] = row.getValue(column.columnDef.id);
					}
				}
				return item;
			}) as Array<Record<string, unknown>>,
			fileName: `export-${new Date().toISOString()}.csv`,
			download: true,
		});
	}

	function handleOnFoldAll() {
		table.setExpanded({});
	}

	/** Render */
	return (
		<React.Fragment>
			{refetch && (
				<Button
					size="xs"
					icon={<Icon name="RefreshCw" />}
					onClick={refetch}
					isLoading={qc.isFetching({ queryKey }) > 0}
					data-cy="data-table-refresh-button"
				/>
			)}
			<Menu
				items={() =>
					menuItems.concat(menuItems.length > 0 ? ["divider"] : [], [
						t("tables.columns"),
						...table.getAllColumns().flatMap((column) =>
							!column.getCanHide()
								? null
								: ["row-selection", "sub-row-handle"].includes(guardString(column.columnDef.id))
									? null
									: ({
											label:
												typeof column.columnDef.header === "string"
													? column.columnDef.header
													: "Spalte",
											onClick: () => column.toggleVisibility(),
											keepOpen: true,
											icon: column.getIsVisible() ? icon("Eye") : icon("EyeOff"),
											isDisabled: !column.getCanHide(),
											iconColor: column.getIsVisible() ? "primary" : "gray",
										} as MenuItemType),
						),
						"divider",
						{
							label: t("tables.items_per_page"),
							onClick: undefined,
							icon: icon("Table"),
							items: PAGINATION_SIZES.map((pageSize) => ({
								label: pageSize.toString(),
								onClick: () => table.setPageSize(pageSize),
								icon: null,
								isActive: table.state.pagination.pageSize === pageSize,
							})),
						},
						"divider",
						t("general.export"),
						{
							label: t("general.export_as_csv"),
							onClick: handleOnGenerateCsv,
							icon: icon("FolderUp"),
						},
						"divider",
						{
							label: t("tables.reset_sorting"),
							onClick: () => {
								table.resetSorting();
							},
							icon: icon("ArrowDownUp"),
						},
						{
							label: t("tables.reset_formatting"),
							onClick: () => {
								handleOnReset(persistenceKey);
							},
							icon: icon("RefreshCw"),
						},
						"divider",
						{
							label: t("misc.monospaced"),
							onClick: handleOnToggleMonospaced,
							icon: icon("Type"),
							isActive: isMonospaced,
						},
						{
							label: t("tables.show_grouping_header"),
							onClick: () => setShowGroupingHeader((prev) => !prev),
							icon: icon("Group"),
							isActive: showGroupingHeader,
						},
						{
							label: t("tables.fold_all"),
							onClick: handleOnFoldAll,
							icon: icon("FoldVertical"),
						},
					])
				}
			>
				<Button
					size="xs"
					icon={<Icon name="EllipsisVertical" />}
					data-cy="data-table-menu-button"
				/>
			</Menu>
		</React.Fragment>
	);
}
